/**
 * Approval Domain Service
 *
 * Domain service for approval operations in the BiteWise healthcare provider platform.
 * Handles approval status retrieval for provider and dietician applications with enhanced
 * error handling, retry logic, and request deduplication.
 *
 * @module services/domains/ApprovalDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager } from '../shared';

/**
 * Approval record interface
 */
export interface ApprovalRecord {
  /** Unique identifier for the approval record */
  id: string;
  /** ID of the admin user who processed the approval */
  adminUserId: string;
  /** ID of the target user (user being approved) */
  targetUserId: string;
  /** Type of approval - provider or dietician */
  approvalType: 'provider' | 'dietician';
  /** Approval status - approved or rejected */
  status: 'approved' | 'rejected';
  /** Optional admin notes about the approval decision */
  notes?: string;
  /** Timestamp when the approval record was created */
  createdAt: string;
  /** Timestamp when the approval record was last updated */
  updatedAt: string;
}

/**
 * API response interface (snake_case from backend)
 */
export interface ApprovalRecordApiResponse {
  id: string;
  admin_user_id: string;
  target_user_id: string;
  approval_type: 'provider' | 'dietician';
  status: 'approved' | 'rejected';
  notes?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Approval domain-specific error class
 */
export class ApprovalApiError extends BaseApiError {
  /** The operation that failed */
  public readonly operation: string;

  constructor(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'ApprovalApiError';
    this.operation = operation;
  }
}

/**
 * Approval API endpoints
 */
const ENDPOINTS = {
  MY_APPROVAL_STATUS: '/approval/me',
} as const;

/**
 * Approval Domain Service
 *
 * Provides approval operations with enhanced error handling,
 * retry logic, and request deduplication.
 */
export class ApprovalDomain extends BaseDomain<ApprovalApiError> {
  constructor() {
    const apiClient = new ApiClient<ApprovalApiError>(
      (message, status, fieldErrors, code) => new ApprovalApiError('API_ERROR', message, status, fieldErrors, code)
    );

    super(apiClient, tokenManager, requestManager);
  }

  /**
   * Get domain name for error messages
   */
  protected getDomainName(): string {
    return 'Approval';
  }

  /**
   * Create domain-specific error instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): ApprovalApiError {
    return new ApprovalApiError(operation, message, status, fieldErrors, code);
  }

  /**
   * Get approval status for the current authenticated user
   *
   * Retrieves all approval records where the current user is the target user.
   * This allows users to check the status of their provider or dietician applications.
   *
   * @returns Promise<ApprovalRecord[]> Array of approval records for the current user
   * @throws ApprovalApiError When request fails or user is not authenticated
   */
  async getMyApprovalStatus(): Promise<ApprovalRecord[]> {
    return this.executeWithDeduplication('getMyApprovalStatus', async () => {
      return this.executeWithRetry(async () => {
        try {
          const response = await this.apiClient.get<ApprovalRecordApiResponse[]>(
            ENDPOINTS.MY_APPROVAL_STATUS
          );
          return response.map(record => this.transformApprovalResponse(record));
        } catch (error) {
          if (error instanceof ApprovalApiError) {
            // Handle specific error cases with user-friendly messages
            if (error.status === 401) {
              throw this.createError(
                'getMyApprovalStatus',
                'Authentication required. Please log in to continue.',
                401,
                undefined,
                'UNAUTHORIZED'
              );
            }
            if (error.status === 403) {
              throw this.createError(
                'getMyApprovalStatus',
                'Access denied. You do not have permission to view approval status.',
                403,
                undefined,
                'FORBIDDEN'
              );
            }
            if (error.status === 500) {
              throw this.createError(
                'getMyApprovalStatus',
                'Server error occurred while retrieving approval status. Please try again later.',
                500,
                undefined,
                'SERVER_ERROR'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Check if user has an approved application for a specific type
   *
   * @param approvalType Type of approval to check for ('provider' or 'dietician')
   * @returns Promise<boolean> True if user has an approved application of the specified type
   */
  async hasApprovedStatus(approvalType: 'provider' | 'dietician'): Promise<boolean> {
    try {
      const approvals = await this.getMyApprovalStatus();
      return approvals.some(approval =>
        approval.approvalType === approvalType && approval.status === 'approved'
      );
    } catch {
      // Return false if we can't determine the approval status
      return false;
    }
  }

  /**
   * Check if user has a rejected application for a specific type
   *
   * @param approvalType Type of approval to check for ('provider' or 'dietician')
   * @returns Promise<boolean> True if user has a rejected application of the specified type
   */
  async hasRejectedStatus(approvalType: 'provider' | 'dietician'): Promise<boolean> {
    try {
      const approvals = await this.getMyApprovalStatus();
      return approvals.some(approval =>
        approval.approvalType === approvalType && approval.status === 'rejected'
      );
    } catch {
      // Return false if we can't determine the approval status
      return false;
    }
  }

  /**
   * Get the most recent approval record for a specific type
   *
   * @param approvalType Type of approval to get ('provider' or 'dietician')
   * @returns Promise<ApprovalRecord | null> Most recent approval record or null if none found
   */
  async getLatestApprovalForType(approvalType: 'provider' | 'dietician'): Promise<ApprovalRecord | null> {
    try {
      const approvals = await this.getMyApprovalStatus();
      const filteredApprovals = approvals.filter(approval => approval.approvalType === approvalType);

      if (filteredApprovals.length === 0) {
        return null;
      }

      // Sort by updatedAt descending to get the most recent
      return filteredApprovals.sort((a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )[0];
    } catch {
      // Return null if we can't retrieve approval data
      return null;
    }
  }

  /**
   * Transform API response to ApprovalRecord (snake_case to camelCase)
   *
   * @private
   */
  private transformApprovalResponse(apiResponse: ApprovalRecordApiResponse): ApprovalRecord {
    return {
      id: apiResponse.id,
      adminUserId: apiResponse.admin_user_id,
      targetUserId: apiResponse.target_user_id,
      approvalType: apiResponse.approval_type,
      status: apiResponse.status,
      notes: apiResponse.notes,
      createdAt: apiResponse.created_at,
      updatedAt: apiResponse.updated_at,
    };
  }
}

/**
 * Default ApprovalDomain singleton instance
 *
 * This singleton provides application-wide access to approval services
 * and maintains consistent state across the entire application.
 */
export const approvalDomain = new ApprovalDomain();